import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { planInputs } from '@goaltree/backend/convex/lib/onboardingFlow'
import { templateDueDate } from '@goaltree/backend/convex/lib/templates'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import { ConvexError } from 'convex/values'
import { ArrowLeft, GitBranch, LayoutList, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AppSidebar } from '@/components/app-sidebar'
import { BlueprintView, StickyFooter } from '@/components/blueprint-view'
import { TemplateBadge } from '@/components/discover-templates'
import GoalBuildLoader from '@/components/goal-build-loader'
import HierarchyFlow from '@/components/hierarchy-flow'
import { type MakeItMineAnswers, MakeItMineSheet } from '@/components/make-it-mine-sheet'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { PlanOutline } from '@/components/plan-outline'
import { SiteHeader } from '@/components/site-header'
import { TemplateHero, blueprintStats, templateStats } from '@/components/template-hero'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useOnboarding } from '@/hooks/use-onboarding'
import { markStartHere } from '@/lib/first-run'
import { goalSlug } from '@/lib/utils'
import { useOnboardingStore } from '@/store/onboarding.store'

export const Route = createFileRoute('/_authenticated/templates/$templateId')({
  component: TemplatePreview,
})

function TemplatePreview() {
  const { t, i18n } = useTranslation()
  const { templateId } = Route.useParams()
  const navigate = useNavigate()
  const locale = i18n.language.split('-')[0]
  const template = useQuery(api.templates.get, { id: templateId as Id<'templates'>, locale })
  const useTemplate = useMutation(api.templates.use)
  const { decompose, streamKey } = useOnboarding()
  const [creating, setCreating] = useState(false)
  const [adapting, setAdapting] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [view, setView] = useState<'list' | 'tree'>('list')
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const isBlueprint = template?.collection === 'blueprints'

  async function handleUse() {
    if (creating || !template) return
    setCreating(true)
    try {
      const goalId = await useTemplate({ id: template.id, locale })
      markStartHere(goalId)
      navigate({ to: '/goals/$goalId', params: { goalId: goalSlug(template.title, goalId) } })
    } catch (err) {
      if (err instanceof ConvexError && String(err.data).includes('Free plan is limited'))
        setUpgradeOpen(true)
      else toast.error(t('templates.useFailed'))
      setCreating(false)
    }
  }

  async function handleAdapt(a: MakeItMineAnswers) {
    if (!template) return
    setSheetOpen(false)
    setAdapting(true)
    const goalText = template.description
      ? `${template.title}: ${template.description}`
      : template.title
    const tree = await decompose(
      goalText,
      {
        ...planInputs({ level: a.level, time: a.time }),
        dueDate: templateDueDate(a.start, template.weeks, a.picked),
      },
      undefined,
      template.structure,
    )
    if (!tree) {
      setAdapting(false)
      return
    }
    const store = useOnboardingStore.getState()
    store.reset()
    store.setGoalText(goalText)
    store.setPlanAnswers({ level: a.level, time: a.time })
    store.setIsPublic(false)
    store.setTemplateId(template.id)
    store.setGeneratedTree(tree)
    navigate({ to: '/goals/new/preview' })
  }

  const viewToggle = (
    <div className="flex self-start rounded-full bg-card p-1 text-[12.5px] ring-1 ring-border">
      {(
        [
          { value: 'list', icon: LayoutList, label: t('goals.viewList') },
          { value: 'tree', icon: GitBranch, label: t('templates.tree') },
        ] as const
      ).map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          type="button"
          onClick={() => setView(value)}
          className={cn(
            'flex cursor-pointer items-center gap-1.5 rounded-full px-3.5 py-1.5 transition-colors',
            view === value
              ? 'bg-muted font-bold text-foreground'
              : 'font-semibold text-muted-foreground hover:text-foreground',
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </button>
      ))}
    </div>
  )

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="relative h-svh overflow-hidden bg-background text-foreground">
        <MobileScreenHeader title={t('templates.title')} showInbox={false} showSearch={false} />
        <SiteHeader className="hidden lg:flex" />
        {adapting ? (
          <GoalBuildLoader goal={template?.title} streamKey={streamKey} />
        ) : (
          <>
            <div className="flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+60px)] lg:pb-6">
              <div className="mx-auto flex max-w-2xl flex-col gap-4 px-4 pt-4 lg:pt-6">
                <div className="flex items-center justify-between gap-2">
                  <Link
                    to="/explore"
                    search={{ tab: 'templates' }}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" /> {t('templates.back')}
                  </Link>
                  {template && <TemplateBadge tpl={template} pill />}
                </div>

                {template === undefined ? (
                  <>
                    <div className="h-2.5 w-20 animate-pulse rounded bg-muted/40" />
                    <div className="h-7 w-2/3 animate-pulse rounded-lg bg-muted/40" />
                    <div className="h-3.5 w-full animate-pulse rounded bg-muted/30" />
                    <div className="h-2.5 w-40 animate-pulse rounded bg-muted/30" />
                    <div className="grid grid-cols-3 gap-2 rounded-xl border border-border bg-card px-3 py-2.5">
                      {[0, 1, 2].map((i) => (
                        <div key={i}>
                          <div className="h-2 w-10 animate-pulse rounded bg-muted/40" />
                          <div className="mt-1.5 h-3 w-16 animate-pulse rounded bg-muted/50" />
                        </div>
                      ))}
                    </div>
                    <div className="h-6 w-28 animate-pulse rounded-lg bg-muted/40" />
                    {[60, 48, 66].map((w) => (
                      <div key={w} className="space-y-2 py-1">
                        <div
                          className="h-3.5 animate-pulse rounded bg-muted/50"
                          style={{ width: `${w}%` }}
                        />
                        <div className="ml-4 h-2.5 w-1/2 animate-pulse rounded bg-muted/30" />
                        <div className="ml-4 h-2.5 w-2/5 animate-pulse rounded bg-muted/30" />
                      </div>
                    ))}
                  </>
                ) : template === null ? (
                  <p className="py-16 text-center text-sm text-muted-foreground/60">
                    {t('templates.notFound')}
                  </p>
                ) : (
                  <>
                    <TemplateHero
                      template={template}
                      stats={isBlueprint ? blueprintStats(t, template) : templateStats(t, template)}
                    />
                    {isBlueprint ? (
                      <BlueprintView template={template} locale={locale} />
                    ) : (
                      <>
                        {template.inspiredBy && (
                          <p className="text-[11px] text-muted-foreground/70">
                            {t('templates.notAffiliated')}
                          </p>
                        )}
                        {template.collection === 'esoteric' && (
                          <p className="text-[11px] text-muted-foreground/70">
                            {t('templates.esotericNote')}
                          </p>
                        )}
                        {viewToggle}
                        {view === 'tree' ? (
                          <div className="h-[60dvh] overflow-hidden rounded-2xl border border-border">
                            <HierarchyFlow tree={template.structure} />
                          </div>
                        ) : (
                          <PlanOutline
                            milestones={template.structure.milestones.map((m, mi) => ({
                              key: String(mi),
                              title: m.title,
                              targets: m.targets.map((tg, ti) => ({
                                key: String(ti),
                                title: tg.title,
                                tasks: tg.tasks.map((task, tki) => ({
                                  key: String(tki),
                                  title: task.title,
                                  effort: task.effort,
                                })),
                              })),
                            }))}
                          />
                        )}
                        <StickyFooter>
                          <button
                            type="button"
                            onClick={handleUse}
                            disabled={creating}
                            title={t('templates.useAsIsHint')}
                            className="flex shrink-0 cursor-pointer items-center gap-2 rounded-full border border-border px-4 py-[13px] text-[13px] font-bold transition-colors hover:bg-muted disabled:opacity-50"
                          >
                            {creating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            {creating ? t('templates.using') : t('templates.useAsIs')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setSheetOpen(true)}
                            disabled={creating}
                            className="flex min-w-0 flex-1 cursor-pointer flex-col items-center justify-center rounded-full bg-primary px-4 py-1.5 text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                          >
                            <span className="text-sm font-extrabold">
                              {t('templates.makeItMine')}
                            </span>
                            <span className="max-w-full truncate text-[10.5px] font-medium opacity-75">
                              {t('templates.makeItMineHint')}
                            </span>
                          </button>
                        </StickyFooter>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
        {template && !isBlueprint && (
          <MakeItMineSheet
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            defaultLevel={template.level}
            onSubmit={(a) => void handleAdapt(a)}
          />
        )}
        <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="goal-limit" />
      </SidebarInset>
    </SidebarProvider>
  )
}
